import { useId } from "react"

import { useQuery, useQueryClient } from "@tanstack/react-query"

import { fetchFileContentApi } from "@/api/api"
import { ApiError } from "@/api/client/_shared"
import { isLiveAuthorizedFilthContentUrl } from "@/api/client/citations"
import { queryKeys } from "@/lib/query-keys"
import { SettingsService } from "@/services/settings-service"

/**
 * True when the backend answered 404 for the file: it is not registered on
 * the session (or document), which no amount of retrying will change. Every
 * other failure (5xx, network, auth) is transient and worth another attempt.
 */
export function isFileNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404
}

/**
 * Retry policy for a file referenced by a finished chat turn: one more
 * attempt, a 404 included. A durable turn's end-of-turn file registration can
 * land just after the completion signal (#3442), so a first 404 right at the
 * turn's end is not yet proof the file is missing; the retry waits for it
 * (finishedTurnRetryDelay). Re-mounts still never refetch a confirmed 404
 * (`retryOnMount: "unless-not-found"`, #2874).
 */
export function finishedTurnRetry(failureCount: number): boolean {
  return failureCount < 1
}

/** How long a finished turn waits before re-asking for a file that answered 404. */
export const FINISHED_TURN_NOT_FOUND_RETRY_DELAY_MS = 3000

function defaultRetryDelay(failureCount: number): number {
  return Math.min(1000 * 2 ** failureCount, 8000)
}

/** Retry delay for a finished chat turn's file: see finishedTurnRetry. */
export function finishedTurnRetryDelay(failureCount: number, error: Error): number {
  return isFileNotFound(error)
    ? FINISHED_TURN_NOT_FOUND_RETRY_DELAY_MS
    : defaultRetryDelay(failureCount)
}

/** How long fetched file bytes stay cached without an observer. */
export const FILE_CONTENT_GC_TIME_MS = 1000 * 60 * 30

/**
 * Finished-turn `cacheScope` for `useFileContent`, keyed by the turn the file
 * was referenced in (message id, or a stable slot key while the id is
 * unknown). A confirmed 404 is terminal for that turn (same-turn remounts
 * must not refetch, #2874), not for `/session-content/{sessionId}/{filename}`
 * for the QueryClient lifetime: a later turn can register the same filename
 * and passes its own id, so it GETs again. There is deliberately no scope
 * without a turn id; a key shared by every turn would pin one turn's 404 on
 * all of them.
 */
/** The query key `useFileContent` registers for `url`, optionally under a cache scope. */
export function fileContentQueryKey(url: string, cacheScope?: string): readonly unknown[] {
  const baseKey = queryKeys.fileContent(url)
  return cacheScope ? [...baseKey, cacheScope] : baseKey
}

export function finishedTurnCacheScope(turnId: string): string {
  return `turn-finished:${turnId}`
}

export type FileContentQueryOptions = {
  /**
   * Retries after the first failed attempt. Defaults to 3 because a file
   * referenced mid-stream may still be on its way to blob storage. A function
   * receives the failure count and the error, like React Query's `retry`.
   */
  retry?: number | boolean | ((failureCount: number, error: Error) => boolean)
  /**
   * Whether a query that already failed is attempted again when another
   * component mounts for the same file. Defaults to true. `"unless-not-found"`
   * re-attempts transient failures on re-mount but not a confirmed 404: once a
   * turn has finished, a file the backend does not know will not appear for
   * that scope, and every re-mount during rendering would otherwise fire a
   * fresh 404 (#2874). Scope per turn/message so a later registration of the
   * same URL is not pinned.
   */
  retryOnMount?: boolean | "unless-not-found"
  /** Delay before each retry, like React Query's `retryDelay`; exponential backoff by default. */
  retryDelay?: (failureCount: number, error: Error) => number
  /**
   * Extra cache-key segment. A miss cached under one scope is invisible to
   * another, so a caller can guarantee one fresh attempt at a well-defined
   * moment (e.g. once a turn has finished and its files are registered)
   * without inheriting a 404 that was cached earlier for the same URL.
   */
  cacheScope?: string
}

/**
 * Hook to fetch file content as ArrayBuffer
 */
export function useFileContent(
  url: string | undefined,
  enabled = true,
  options: FileContentQueryOptions = {}
) {
  const settingsService = SettingsService.getInstance()
  const queryClient = useQueryClient()
  const viewerInstanceId = useId()
  const requiresLiveAuthorization = !!url && isLiveAuthorizedFilthContentUrl(url)
  // Read only on the live-authorization path. Callers that never touch a FILTH
  // content URL are not required to populate claims, and an unconditional
  // dereference here crashes them.
  const claims = requiresLiveAuthorization ? settingsService.claims : undefined
  const principalKey = `${claims?.tid || ""}:${claims?.oid || claims?.sub || ""}`

  // Both key extensions compose: `cacheScope` isolates a turn's cached 404
  // (#2874), and the live-authorization segments isolate a FILTH content
  // handle per principal and per viewer instance so no other principal --
  // and no later mount -- can be served a body the backend authorized once.
  // `viewerInstanceId` is the load-bearing half: it is unique per mount, so
  // even an empty principalKey cannot produce a key shared across principals.
  const scopedKey = fileContentQueryKey(url || "", options.cacheScope)
  const queryKey: readonly unknown[] = requiresLiveAuthorization
    ? [...scopedKey, "live-authorization", principalKey, viewerInstanceId]
    : scopedKey

  // Computed after the key above: `getQueryState` must read the same key the
  // query below registers, or it inspects a different entry's error.
  let retryOnMount: boolean
  if (options.retryOnMount === "unless-not-found") {
    retryOnMount = !isFileNotFound(queryClient.getQueryState(queryKey)?.error)
  } else {
    retryOnMount = options.retryOnMount ?? true
  }

  return useQuery({
    queryKey,
    queryFn: () => fetchFileContentApi(url!, settingsService.token),
    enabled: enabled && !!url,
    staleTime: requiresLiveAuthorization ? 0 : 1000 * 60 * 10,
    gcTime: requiresLiveAuthorization ? 0 : FILE_CONTENT_GC_TIME_MS,
    refetchOnMount: requiresLiveAuthorization ? "always" : true,
    refetchOnWindowFocus: false,
    // Override global retry:false — file content may not be available immediately
    // (e.g., code interpreter images uploaded to blob during streaming)
    // A live-authorized FILTH fetch is never retried: a denial is an
    // authorization answer, not a transient failure, so it must not be
    // re-attempted behind the user's back. Everything else keeps the
    // caller's policy.
    retry: requiresLiveAuthorization ? false : (options.retry ?? 3),
    retryOnMount,
    retryDelay: options.retryDelay ?? defaultRetryDelay,
  })
}

/**
 * Hook to fetch file content as text (decoded from ArrayBuffer)
 */
export function useFileContentAsText(url: string | undefined, enabled = true) {
  const { data: buffer, ...rest } = useFileContent(url, enabled)

  return {
    ...rest,
    data: buffer ? new TextDecoder("utf-8").decode(buffer) : undefined,
  }
}
